/**
 * Profile Form Component
 *
 * Allows users to update their display name and timezone settings.
 */

import React, { useState } from 'react';
import {
  Card,
  Title,
  Text,
  Stack,
  TextInput,
  Select,
  Button,
  Alert,
  Group,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../api/client';

// Comprehensive timezone list for global reef aquarium hobbyists
const TIMEZONES = [
  // Universal
  { value: 'UTC', label: 'UTC' },
  // Americas
  { value: 'America/New_York', label: 'New York (EST/EDT)' },
  { value: 'America/Chicago', label: 'Chicago (CST/CDT)' },
  { value: 'America/Denver', label: 'Denver (MST/MDT)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PST/PDT)' },
  { value: 'America/Anchorage', label: 'Anchorage (AKST/AKDT)' },
  { value: 'America/Phoenix', label: 'Phoenix (MST)' },
  { value: 'America/Toronto', label: 'Toronto (EST/EDT)' },
  { value: 'America/Vancouver', label: 'Vancouver (PST/PDT)' },
  { value: 'America/Mexico_City', label: 'Mexico City (CST/CDT)' },
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT)' },
  { value: 'Pacific/Honolulu', label: 'Honolulu (HST)' },
  // Europe
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Europe/Amsterdam', label: 'Amsterdam (CET/CEST)' },
  { value: 'Europe/Madrid', label: 'Madrid (CET/CEST)' },
  { value: 'Europe/Rome', label: 'Rome (CET/CEST)' },
  { value: 'Europe/Stockholm', label: 'Stockholm (CET/CEST)' },
  { value: 'Europe/Warsaw', label: 'Warsaw (CET/CEST)' },
  { value: 'Europe/Athens', label: 'Athens (EET/EEST)' },
  { value: 'Europe/Moscow', label: 'Moscow (MSK)' },
  // Asia
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'Mumbai/Kolkata (IST)' },
  { value: 'Asia/Bangkok', label: 'Bangkok (ICT)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (HKT)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Seoul', label: 'Seoul (KST)' },
  { value: 'Asia/Jakarta', label: 'Jakarta (WIB)' },
  { value: 'Asia/Manila', label: 'Manila (PHT)' },
  // Oceania
  { value: 'Australia/Perth', label: 'Perth (AWST)' },
  { value: 'Australia/Adelaide', label: 'Adelaide (ACST/ACDT)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
  { value: 'Pacific/Fiji', label: 'Fiji (FJT)' },
  // Africa / Middle East
  { value: 'Africa/Cairo', label: 'Cairo (EET)' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg (SAST)' },
  { value: 'Africa/Lagos', label: 'Lagos (WAT)' },
  { value: 'Asia/Jerusalem', label: 'Jerusalem (IST)' },
];

const VOLUME_UNITS = [
  { value: 'litres', label: 'Litres' },
  { value: 'gallons', label: 'Gallons' },
];

interface ProfileFormData {
  display_name: string;
  timezone: string;
  volume_unit: string;
}

export const ProfileForm: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const form = useForm<ProfileFormData>({
    initialValues: {
      display_name: user?.display_name || '',
      timezone: user?.timezone || 'UTC',
      volume_unit: user?.volume_unit || 'litres',
    },
    validate: {
      display_name: (value) =>
        value.length > 0 ? null : 'Display name is required',
    },
  });

  const handleSubmit = async (values: ProfileFormData) => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await api.put('/api/auth/me', values);
      setSuccess(true);
      // Refresh user data to reflect changes
      await refreshUser();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to update profile';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card withBorder p="lg">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <div>
            <Title order={4}>Profile Information</Title>
            <Text size="sm" c="dimmed">
              Update your display name, timezone, and volume unit preferences.
            </Text>
          </div>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red">
              {error}
            </Alert>
          )}

          {success && (
            <Alert icon={<IconCheck size={16} />} color="green">
              Profile updated successfully.
            </Alert>
          )}

          <TextInput
            label="Email"
            value={user?.email || ''}
            disabled
            description="Email cannot be changed"
          />

          <TextInput
            label="Display Name"
            placeholder="Enter your display name"
            required
            {...form.getInputProps('display_name')}
          />

          <Select
            label="Timezone"
            description="Used for scheduling daily data collection"
            placeholder="Select your timezone"
            data={TIMEZONES}
            searchable
            required
            {...form.getInputProps('timezone')}
          />

          <Select
            label="Volume Unit"
            description="Default unit for tank volumes and water changes"
            data={VOLUME_UNITS}
            required
            {...form.getInputProps('volume_unit')}
          />

          <Group justify="flex-end">
            <Button type="submit" loading={saving}>
              Save Changes
            </Button>
          </Group>
        </Stack>
      </form>
    </Card>
  );
};
